import type { ResolvedSkillMetadata } from '@hector/shared-types';
import { Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { sortSkillsForPalette } from './skillUtils';

interface SkillSlashPaletteProps {
  skills: ResolvedSkillMetadata[];
  activeSlug: string | null;
  onSelect(skill: ResolvedSkillMetadata): void;
}

const CUSTOMIZATION_LABELS = {
  hector: 'Par défaut',
  overridden: 'Personnalisée',
  personal: 'Personnalisée',
} as const;

export function SkillSlashPalette({ skills, activeSlug, onSelect }: SkillSlashPaletteProps) {
  // A disabled entry may briefly survive in the query cache during a refetch.
  // Filtering here keeps the palette non-executable even in that transient state.
  const executableSkills = sortSkillsForPalette(skills.filter(
    (skill) => skill.isActive && skill.availability === 'active',
  ));

  if (executableSkills.length === 0) {
    return (
      <div className="absolute bottom-full left-2 z-40 mb-3 w-[calc(100%-1rem)] rounded-xl border border-grey-200 bg-white p-3 text-xs text-grey-500 shadow-[0_12px_28px_rgba(0,0,0,0.12)]">
        Aucune compétence correspondante.
      </div>
    );
  }

  return (
    <div className="absolute bottom-full left-2 z-40 mb-3 w-[calc(100%-1rem)] overflow-hidden rounded-xl border border-grey-200 bg-white shadow-[0_12px_28px_rgba(0,0,0,0.12)]" role="listbox" aria-label="Compétences disponibles">
      <div className="max-h-[13.5rem] overflow-y-auto p-1">
        {executableSkills.map((skill) => (
          <button
            key={skill.slug}
            type="button"
            role="option"
            aria-selected={skill.slug === activeSlug}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => onSelect(skill)}
            className={cn(
              'flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-left transition',
              skill.slug === activeSlug ? 'bg-grey-100' : 'hover:bg-grey-50',
            )}
          >
            <Sparkles className="h-3 w-3 shrink-0 text-grey-400" />
            <span className="w-36 shrink-0 truncate font-mono text-xs font-medium text-grey-900">/{skill.slug}</span>
            <span className="min-w-0 flex-1 truncate text-xs leading-normal text-grey-500">{skill.description}</span>
            <span className="shrink-0 rounded-full bg-grey-100 px-1.5 py-0.5 text-[10px] font-medium text-grey-500">
              {CUSTOMIZATION_LABELS[skill.customization]}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
